import type { ListingDetails } from 'app/types/listingDetails.type';
import type { CondensedAmenities, Model } from 'app/types/commonTypes.type';
import { listingUtils_getDistanceToUserPoint } from 'app/shared/utils/listingUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import { getListingPhotoUrls } from 'app/shared/utils/fedPhotos';

export const STATIC_CATEGORIES = [
  'Distance',
  'Pets',
  'Laundry',
  'Security',
  'A/C',
  'Dishwasher',
  'Parking',
  'Gym',
  'Pool',
] as const;

export type StaticCategory = (typeof STATIC_CATEGORIES)[number];

export type BedroomLabel = 'Studio' | '1 bed' | '2 beds' | '3 beds' | '4+ beds';

const BEDROOM_MAP: Record<number, BedroomLabel> = {
  0: 'Studio',
  1: '1 bed',
  2: '2 beds',
  3: '3 beds',
  4: '4+ beds',
};

export type CellValue = string | boolean | null;

export type ComparisonRow = {
  /** Listing this column represents (current listing first, then similar listings). */
  listing: ListingDetails;
  title: string;
  image: string;
  link: string | null;
  /** Bedroom-keyed prices (e.g. "Studio" -> "$1,200+"). */
  prices: Partial<Record<BedroomLabel, string | null>>;
  /** Static amenity rows. */
  amenities: Record<StaticCategory, CellValue>;
};

const canRetainCurrentListingDetails = (listing: ListingDetails): boolean =>
  listing.active !== false || listing.isAllowedToRetain !== false;

const getDistanceLabel = (current: ListingDetails, similar: ListingDetails): string | null => {
  const cGeo = current.geo;
  const sGeo = similar.geo;
  if (!cGeo || !sGeo) return null;

  if (cGeo.lat === sGeo.lat && cGeo.lon === sGeo.lon) return null;

  const miles = listingUtils_getDistanceToUserPoint({
    originLat: cGeo.lat,
    originLon: cGeo.lon,
    lat0: sGeo.lat,
    lon0: sGeo.lon,
  });

  if (miles == null || miles < 0.1) return null;
  if (miles.toFixed(1) === '1.0') return `${miles.toFixed(0)} mile away`;
  return `${miles.toFixed(1)} miles away`;
};

/**
 * Returns the bedroom labels covering the min/max range across all listings.
 * Anything 4+ collapses into a single "4+ beds" bucket.
 */
export const getBedroomLabels = (listings: ReadonlyArray<ListingDetails>): Array<BedroomLabel> => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  listings.forEach((listing) => {
    const minMax = listing.listingMinMaxPriceBeds;
    if (!minMax) return;
    if (minMax.minBeds < min) min = minMax.minBeds;
    if (minMax.maxBeds > max) max = minMax.maxBeds;
  });

  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];

  const seen = new Set<BedroomLabel>();
  const ordered: Array<BedroomLabel> = [];
  for (let n = min; n <= max; n++) {
    const label = BEDROOM_MAP[n] ?? BEDROOM_MAP[4];
    if (!seen.has(label)) {
      seen.add(label);
      ordered.push(label);
    }
  }
  return ordered;
};

type PricingModel = { numBeds: number; lowPrice?: number | null; highPrice?: number | null };

// Current listings carry `modelsAndPricing`; similar listings come from byCoords/SummaryV2 and
// expose `models` instead. The reducer types both as ListingDetails, so widen at this single
// boundary instead of casting at each read site.
type ListingWithPricing = ListingDetails & { models?: Array<Model> };

const getPricingModels = (listing: ListingDetails): Array<PricingModel> => {
  const withPricing = listing as ListingWithPricing;
  return withPricing.modelsAndPricing ?? withPricing.models ?? [];
};

const getPricesByBedroom = (
  listing: ListingDetails,
  labels: ReadonlyArray<BedroomLabel>,
): Partial<Record<BedroomLabel, string | null>> => {
  const result: Partial<Record<BedroomLabel, string | null>> = {};
  labels.forEach((label) => {
    result[label] = null;
  });

  const models = getPricingModels(listing);

  models.forEach((model) => {
    const label = BEDROOM_MAP[model.numBeds] ?? BEDROOM_MAP[4];
    if (!labels.includes(label)) return;

    const { lowPrice, highPrice } = model;
    if (lowPrice == null && highPrice == null) {
      result[label] = 'Available';
      return;
    }

    const low = numberUtils.asCurrency(lowPrice ?? 0);
    if (highPrice == null || lowPrice === highPrice) {
      result[label] = low;
    } else {
      result[label] = `${low}+`;
    }
  });

  return result;
};

const getAmenityRow = (amenities: CondensedAmenities | undefined): Record<StaticCategory, CellValue> => ({
  Distance: null,
  Pets: amenities?.pets ?? null,
  Laundry: amenities?.laundry ?? null,
  Security: amenities?.security ?? null,
  'A/C': amenities?.airConditioning ?? null,
  Dishwasher: amenities?.dishwasher ?? null,
  Parking: amenities?.parking ?? null,
  Gym: amenities?.gym ?? null,
  Pool: amenities?.pool ?? null,
});

export const buildComparisonRows = (
  currentListing: ListingDetails,
  similarListings: ReadonlyArray<ListingDetails>,
): {
  rows: Array<ComparisonRow>;
  bedroomLabels: Array<BedroomLabel>;
  hasCurrentListingColumn: boolean;
} => {
  const hasCurrentListingColumn = canRetainCurrentListingDetails(currentListing);
  const visibleListings = hasCurrentListingColumn ? [currentListing, ...similarListings] : similarListings;
  const bedroomLabels = getBedroomLabels(visibleListings);

  const rows: Array<ComparisonRow> = visibleListings.map((listing, index) => {
    const isCurrent = hasCurrentListingColumn && index === 0;
    const hideStreet = listing.address?.hideStreet === true;
    const title = hideStreet ? '(Undisclosed Address)' : listing.displayName || listing.address?.street || '';
    const image = getListingPhotoUrls(listing)[0] || listing.previewPhoto?.url || '';
    const link = isCurrent ? null : listing.uriV2 || null;

    const amenities = getAmenityRow(listing.amenities?.condensedAmenities);
    if (hasCurrentListingColumn && !isCurrent) {
      amenities.Distance = getDistanceLabel(currentListing, listing);
    }

    return {
      listing,
      title,
      image,
      link,
      prices: getPricesByBedroom(listing, bedroomLabels),
      amenities,
    };
  });

  return { rows, bedroomLabels, hasCurrentListingColumn };
};

export const formatCellValue = (value: CellValue, placeholder = '—'): string => {
  if (value == null) return placeholder;
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return value;
};
